import { createSlice } from '@reduxjs/toolkit';
import { REDUX_STORE_SLICES } from '../../services/exports/Constants';
import { BasketInterface, BasketLineItem } from '../types';
import { ActionPayloadInterface } from '../../services/exports/Interfaces';
import collect from 'collect.js';

interface AddLineItemPayloadInterface {
  company_id: number;
  item: BasketLineItem;
}

interface AddLineItemsPayloadInterface {
  company_id: number;
  items: BasketLineItem[];
}

interface RemoveLineItemPayloadInterface {
  company_id: number;
  index: number;
}

interface RemoveLineItemsByProductIdsPayloadInterface {
  company_id: number;
  product_ids: number[];
}

interface UpdateLineItemPayloadInterface {
  company_id: number;
  index: number;
  item: Partial<BasketLineItem>;
}

interface ClearBasketPayloadInterface {
  company_id: number;
}

interface SetBasketPayloadInterface {
  company_id: number;
  items: BasketLineItem[];
}

const initialState: BasketInterface = {
  lineItems: {},
};

const basketSlice = createSlice({
  name: REDUX_STORE_SLICES.BASKET,
  initialState,
  reducers: {
    addItem: (state: BasketInterface, action: ActionPayloadInterface<AddLineItemPayloadInterface>): void => {
      const { company_id, item } = action.payload;

      state.lineItems = {
        ...state.lineItems,
        [company_id]: [...(state.lineItems[company_id] ?? []), item],
      };
    },
    addItems: (state: BasketInterface, action: ActionPayloadInterface<AddLineItemsPayloadInterface>): void => {
      const { company_id, items } = action.payload;

      state.lineItems = {
        ...state.lineItems,
        [company_id]: [...(state.lineItems[company_id] ?? []), ...items],
      };
    },
    removeItem: (state: BasketInterface, action: ActionPayloadInterface<RemoveLineItemPayloadInterface>): void => {
      const { company_id, index } = action.payload;

      state.lineItems = {
        ...state.lineItems,
        [company_id]: collect(state.lineItems[company_id] ?? [])
          .forget(index)
          .toArray(),
      };
    },
    removeItemsByProductIds: (
      state: BasketInterface,
      action: ActionPayloadInterface<RemoveLineItemsByProductIdsPayloadInterface>,
    ): void => {
      const { company_id, product_ids } = action.payload;

      state.lineItems = {
        ...state.lineItems,
        [company_id]: collect(state.lineItems[company_id] ?? [])
          .whereNotIn('product_id', product_ids)
          .toArray(),
      };
    },
    updateItem: (state: BasketInterface, action: ActionPayloadInterface<UpdateLineItemPayloadInterface>): void => {
      const { company_id, index, item } = action.payload;

      state.lineItems = {
        ...state.lineItems,
        [company_id]: collect(state.lineItems[company_id] ?? [])
          .put(index, {
            ...state.lineItems[company_id][index],
            ...item,
          })
          .toArray(),
      };
    },
    clear: (state: BasketInterface, action: ActionPayloadInterface<ClearBasketPayloadInterface>): void => {
      const { company_id } = action.payload;

      state.lineItems = {
        ...state.lineItems,
        [company_id]: [],
      };
    },
    set: (state: BasketInterface, action: ActionPayloadInterface<SetBasketPayloadInterface>): void => {
      const { company_id, items } = action.payload;

      state.lineItems = {
        ...state.lineItems,
        [company_id]: items,
      };
    },
  },
});

export default basketSlice;

export const { removeItemsByProductIds } = basketSlice.actions;
